import React, { useCallback } from 'react';
import {
  Box,
  TableCell,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core';
import { LinkComponent } from 'src/components';
import { useNavigate } from 'react-router';
import { colors } from 'src/constants';
import { slices, useAppDispatch, useAppSelector } from 'src/redux';
import {
  RMAColumn,
  RMAData,
  RMAUpdateBranchParamsOnAllowedStatus
} from 'src/redux/slices/rma/types';
import { initialRMATableColumns } from 'src/redux/slices/rma/constants/rma-table-columns';
import { RMAField } from 'src/redux/slices/rma/constants/rma-fields';
import {
  allowedStatusThatRequiredBranch,
  rmaOptionsStatuses,
  rmaOptionsStatusesV2,
  rmaStatuesV2
  // rmaLocationOptions
} from 'src/redux/slices/rma/constants/rma-form-fields';
import { AlternatingColorTableRow } from 'src/components/table/AlternatingColorTableRow';
import { Autocomplete } from '@material-ui/lab';
import { unwrapResult } from '@reduxjs/toolkit';
import { RMAImages } from './RMAImages';
import LinkIcon from '@material-ui/icons/Link';
import { useSnackBar } from 'src/hooks';
import { generatePublicLink } from 'src/utils';

interface Props {
  rmaData: RMAData;
  permissions?: string[];
  branch_name?: string;
  has_multiple_branch: boolean;
  onHandleStatusNeedBranch?: (
    params: RMAUpdateBranchParamsOnAllowedStatus
  ) => void;
}

// const { selectors: taptapCustomerSelectors } = slices.taptapCustomer;
const { actions: rmaActions, selectors: rmaSelector } = slices.rma;

const RMATableRow = ({
  rmaData,
  permissions,
  branch_name,
  has_multiple_branch = false,
  onHandleStatusNeedBranch
}: Props) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const snackBar = useSnackBar();

  const rmaColumnsRedux = useAppSelector(rmaSelector.selectRMAColumns);

  const generateLink = useCallback(
    async (serialNo: string, transactionNo: string) => {
      if (!serialNo && !transactionNo) {
        snackBar.show({
          severity: 'error',
          message:
            'Failed to generate link. Serial No. and Transaction No. are not available'
        });
        return;
      }

      await generatePublicLink(serialNo, transactionNo);
      snackBar.show({
        severity: 'success',
        message: 'Link copied to clipboard'
      });
    },
    [snackBar]
  );

  const handleFieldChange = useCallback(
    async (fieldName: string, newValue?: string) => {
      const rmaID = rmaData?.rma_id;

      if (
        fieldName === 'status' &&
        allowedStatusThatRequiredBranch?.includes(newValue || '') &&
        has_multiple_branch
      ) {
        if (onHandleStatusNeedBranch) {
          onHandleStatusNeedBranch({
            rma_id: Number(rmaID) || 0,
            status: newValue || '',
            openBranchUpdate: true
          });
        }
        return;
      }

      const updateRmaRes = unwrapResult(
        await dispatch(
          rmaActions.updateRmaThunk({
            [fieldName]: newValue,
            rma_id: Number(rmaID),
            branch_name: allowedStatusThatRequiredBranch?.includes(
              newValue || ''
            )
              ? branch_name
              : undefined
          })
        )
      );

      if (!updateRmaRes?.success) {
        const { message, errors } = updateRmaRes.originalData;

        snackBar.show({
          severity: 'error',
          message: Object.values(errors)
            ? Object.values(errors).join(', ')
            : message ||
              'Failed to update rma, please try again later or contact support.'
        });
        return;
      }
    },
    [
      snackBar,
      branch_name,
      rmaData,
      has_multiple_branch,
      dispatch,
      onHandleStatusNeedBranch
    ]
  );

  const customBgCellColor = useCallback(
    (column: RMAColumn) => {
      if (column.sticky) {
        return colors.grey[100];
      }
      if (column.field === RMAField.STATUS) {
        const statusBgColor = [
          ...rmaOptionsStatuses,
          ...rmaOptionsStatusesV2
        ]?.find((status) => status.value === rmaData?.status)?.bgColor;

        return statusBgColor || 'inherit';
      }
      return 'inherit';
    },
    [rmaData.status]
  );

  const RenderCustomRow = useCallback(
    (fieldName: string) => {
      if (fieldName === RMAField.SERIAL_NO) {
        return (
          <Box display="flex" alignItems="center">
            {rmaData?.serial_no || rmaData?.transaction_no ? (
              <Tooltip title="Copy link to clipboard">
                <LinkIcon
                  fontSize="small"
                  color="action"
                  style={{ cursor: 'pointer', marginRight: 10 }}
                  onClick={() => {
                    if (rmaData?.transaction_no) {
                      generateLink(
                        rmaData?.serial_no ?? '',
                        rmaData?.transaction_no ?? ''
                      );
                    }
                  }}
                />
              </Tooltip>
            ) : null}

            <LinkComponent
              href={`/app/rma/${rmaData?.rma_id}`}
              openInNewTab
              onClick={() => navigate(`/app/rma/${rmaData?.rma_id}`)}
              title={
                <Typography
                  variant="body2"
                  style={{
                    color: rmaData?.serial_no ? '#1976d2' : '#9e9e9e',
                    textDecoration: rmaData?.serial_no ? 'underline' : 'none',
                    cursor: rmaData?.serial_no ? 'pointer' : 'default'
                  }}
                >
                  {rmaData?.serial_no || '-- Not Available'}
                </Typography>
              }
            />
          </Box>
        );
      }
      if (fieldName === RMAField.ID || fieldName === RMAField.RMA_ID) {
        return (
          <Box display="flex" alignItems="center">
            <LinkComponent
              href={`/app/rma/${rmaData?.rma_id}`}
              openInNewTab
              onClick={() => navigate(`/app/rma/${rmaData?.rma_id}`)}
              title={
                <Typography
                  variant="body2"
                  style={{
                    color: colors.link,
                    textDecoration: 'underline',
                    cursor: 'pointer'
                  }}
                >
                  {rmaData?.rma_id}
                </Typography>
              }
            />
          </Box>
        );
      }
    },
    [rmaData, navigate, generateLink]
  );

  const RenderAutoComplete = useCallback(
    (autoCompleteCol: RMAColumn, rmaData: RMAData) => {
      const handlePreventGetNextPageScroll = (event: React.UIEvent<HTMLElement>) => {
        event.stopPropagation();
      };

      const deprecated_status: any = {
        label: rmaData?.status,
        value: rmaData?.status,
        disabled: false,
        deprecated: false,
        allowed_permission: []
      };

      const autoCompleteValue = autoCompleteCol.options?.find(
        (x) => x.value === rmaData[autoCompleteCol.field]
      );

      const filterColOptions = autoCompleteCol?.options?.filter(
        (data) =>
          !data?.deprecated &&
          data?.allowed_permission?.some((permission) =>
            permissions?.includes(permission)
          ) &&
          // restrict changing to replace item status on this page
          data.value !== rmaStatuesV2.SC_FOR_RETURN_REPLACED_SAME &&
          data.value !== rmaStatuesV2.SC_FOR_RETURN_REPLACED_OTHER
      );

      return (
        <Autocomplete
          ListboxProps={{
            onScroll: handlePreventGetNextPageScroll
          }}
          options={
            autoCompleteCol?.field === 'status'
              ? filterColOptions || []
              : autoCompleteCol?.options || []
          }
          getOptionLabel={(option) => option.label}
          getOptionDisabled={(option) =>
            Boolean(option?.deprecated || option?.disabled)
          }
          renderOption={(option) => (
            <Tooltip
              title={option.deprecated ? 'This option is deprecated' : ''}
              placement="right"
            >
              <span style={{ opacity: option.deprecated ? 0.5 : 1 }}>
                {option.label}
              </span>
            </Tooltip>
          )}
          value={autoCompleteValue || deprecated_status}
          onChange={(event, newValue) => {
            handleFieldChange(autoCompleteCol.field, newValue?.value);
          }}
          renderInput={(params) => <TextField {...params} variant="outlined" />}
        />
      );
    },
    [handleFieldChange, permissions]
  );

  // eslint-disable-next-line no-unused-vars
  const RenderRows = useCallback(
    () =>
      initialRMATableColumns?.map((column) => {
        if (!rmaColumnsRedux[column.name]) {
          return null;
        }

        return (
          <TableCell
            style={{
              position: column.sticky ? 'sticky' : 'static',
              left: column.sticky ? 0 : 'auto',
              backgroundColor: customBgCellColor(column),
              zIndex: column.sticky ? 1 : 'auto',
              borderRight: `1px solid ${colors.grey[300]}`,
              minHeight: 20,
              minWidth: column.minWidth ? column.minWidth : 200
            }}
            key={column.field}
          >
            {column?.type === 'custom' && RenderCustomRow(column.field)}
            {column?.type === 'text' && rmaData[column.field]}
            {column?.type === 'link' && (
              <LinkComponent
                openInNewTab
                href={`transaction/${rmaData[column.field]}`}
                title={rmaData[column.field]}
              />
            )}
            {column?.type === 'autocomplete' &&
              RenderAutoComplete(column, rmaData)}
            {column?.type === 'image' && (
              <RMAImages rmaData={rmaData} columnWithImgs={column} />
            )}
            {/* {column?.type === 'date' &&
              formatDate(customer[column.field], 'MMM d, yyyy h:mm a')} */}
            {/* {column?.type === 'currency' &&
              formatCurrency(customer[column.field])} */}
            {/* {column?.type === 'boolean' && (
              <div>
                {customer[column.field] === true ? (
                  <CheckIcon style={{ color: colors.success }} />
                ) : (
                  <ClearIcon style={{ color: colors.error }} />
                )}
              </div>
            )} */}
          </TableCell>
        );
      }, []),
    [
      RenderAutoComplete,
      RenderCustomRow,
      customBgCellColor,
      rmaColumnsRedux,
      rmaData
    ]
  );

  return (
    <React.Fragment key={rmaData?.id}>
      <AlternatingColorTableRow hover>{RenderRows()}</AlternatingColorTableRow>
    </React.Fragment>
  );
};

export default RMATableRow;
